import React, { useCallback } from 'react';
import DuplicationDialogContainer from './DuplicationDialogContainer';
import PropTypes from 'prop-types';
import { useMutation } from 'features/common/security/hooks';
import { useSimpleError, useLocalSubscription } from 'hooks';
import { DUPLICATE_INSURANCE_DOCUMENT } from '../mutations';
import { INSURANCE_DOCUMENT_DUPLICATED } from '../subscriptions';
import { useTranslation } from 'react-i18next';
import { useToast } from 'hooks';
import { set } from '@totalsoft/change-tracking-react';
import { Link } from '@mui/material';
import { useHistory } from 'react-router-dom';

const DuplicationContainer = ({ openConfirmDuplication, onCloseConfirmationDialog, documentId, duplicationInProgress }) => {
  const { t } = useTranslation();
  const addToast = useToast();
  const showError = useSimpleError();
  const history = useHistory();

  const [duplicateInsuranceDocument] = useMutation(DUPLICATE_INSURANCE_DOCUMENT, {
    onError: error => {
      showError(error);
      (duplicationInProgress |> set)(false);
    }
  });

  const handleDuplicateInsuranceDocument = useCallback(() => {
    (duplicationInProgress |> set)(true);
    duplicateInsuranceDocument({
      variables: {
        input: {
          documentId
        }
      }
    });
    onCloseConfirmationDialog();
  }, [documentId, duplicateInsuranceDocument, onCloseConfirmationDialog, duplicationInProgress]);

  useLocalSubscription(INSURANCE_DOCUMENT_DUPLICATED, {
    variables: {
      sourceDocumentId: documentId
    },
    onData: ({ data }) => {
      const { documentNumber, documentId } = data.insuranceDocumentDuplicated;
      (duplicationInProgress |> set)(false);

      const createdDocumentText = t('Offer.Duplication.DocumentCreated', { documentNumber });
      const parts = createdDocumentText.split(documentNumber);
      const createdDocumentLink = (
        <Link
          component='button'
          underline='hover'
          onClick={() => {
            history.push(`/insurance-documents/${documentId}/generalData`);
          }}
        >
          {documentNumber}
        </Link>
      );

      const toastContent = (
        <>
          <div>
            {parts[0]}
            {createdDocumentLink}
            {parts[1]}
          </div>
        </>
      );

      addToast(toastContent, 'success', { toastId: 'documentDuplicatedToast' });
    }
  });

  return (
    <>
      {openConfirmDuplication && (
        <DuplicationDialogContainer
          onCloseConfirmationDialog={onCloseConfirmationDialog}
          onConfirmDialog={handleDuplicateInsuranceDocument}
          documentId={documentId}
        />
      )}
    </>
  );
};

DuplicationContainer.propTypes = {
  openConfirmDuplication: PropTypes.bool.isRequired,
  onCloseConfirmationDialog: PropTypes.func.isRequired,
  documentId: PropTypes.number.isRequired,
  duplicationInProgress: PropTypes.object.isRequired
};

export default DuplicationContainer;
